import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Table } from 'react-bootstrap';
import { 
  FaBoxes, 
  FaClipboardList, 
  FaIdBadge, 
  FaBuilding, 
  FaCalendarAlt,
  FaChevronRight,
  FaChartLine,
  FaUser,
  FaShoppingCart,
  FaInfoCircle
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../supabaseClient';
import '../../styles/dashboard.css';

// Em um ambiente real, você buscaria esses dados da API
const mockEvents = [
  { id: 1, name: 'Expo Negócios 2025', date: '15/08/2025', location: 'Centro de Convenções', status: 'confirmed' },
  { id: 2, name: 'Feira de Tecnologia', date: '22/09/2025', location: 'Expo Center Norte', status: 'pending' },
];

const mockOrders = [
  { id: 'ORD-2024-001', status: 'Aprovado', date: '10/06/2025', totalValue: 'R$ 5.800,00' },
  { id: 'ORD-2024-002', status: 'Em análise', date: '25/06/2025', totalValue: 'R$ 3.200,00' },
];

const ExpositorHomePage = () => {
  // Usando o novo formato de autenticação
  const { user, userProfile, hasRole } = useAuth();
  const [expositorData, setExpositorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(null);

  useEffect(() => {
    const loadExpositorData = async () => {
      try {
        setLoading(true);
        setDbError(null);

        let empresaData = null;

        // Se for expositor, tentar buscar dados da empresa no banco
        if (hasRole('expositor') && user?.id) {
          try {
            console.log('🔍 Buscando dados do expositor no banco...');
            console.log('🔍 User ID:', user.id);
            
            const { data: expositorInfo, error } = await supabase
              .from('caex_expositores')
              .select('*')
              .eq('usuario_id', user.id)  // ✅ Campo correto
              .eq('ativo', true)          // ✅ Campo correto
              .maybeSingle();

            if (error) {
              console.warn('⚠️ Erro ao buscar dados do expositor:', error.message);
              setDbError(`Erro na consulta: ${error.message}`);
            } else if (expositorInfo) {
              empresaData = {
                companyName: expositorInfo.nome_empresa,
                cnpj: expositorInfo.cnpj,
                contactName: expositorInfo.responsavel,
                email: expositorInfo.email,
                phone: expositorInfo.telefone || 'Não informado',
                address: expositorInfo.endereco || 'Endereço não informado',
                participatingEvents: mockEvents,
                recentOrders: mockOrders,
                credenciais: 8,
              };
              console.log('✅ Dados do expositor carregados do banco:', expositorInfo.nome_empresa);
            } else {
              console.log('⚠️ Nenhum expositor encontrado para este usuário');
              setDbError('Usuário não está cadastrado como expositor no sistema');
            }

          } catch (error) {
            console.error('💥 Erro inesperado ao buscar dados do expositor:', error);
            setDbError(`Erro inesperado: ${error.message}`);
          }
        }

        // Fallback para dados do perfil se não encontrou no banco ou não é expositor
        if (!empresaData) {
          empresaData = {
            companyName: 'Empresa não vinculada',
            cnpj: 'Não informado',
            contactName: userProfile?.full_name || user?.full_name || user?.name || 'Usuário',
            email: userProfile?.email || user?.email || 'email@exemplo.com',
            phone: 'Não informado',
            address: 'Endereço não informado',
            participatingEvents: mockEvents,
            recentOrders: mockOrders,
            credenciais: 0,
          };
          console.log('📋 Usando dados do perfil do usuário:', userProfile?.full_name);
        }

        setExpositorData(empresaData);
      } catch (error) {
        console.error('💥 Erro geral ao carregar dados do expositor:', error);
        setDbError(`Erro geral: ${error.message}`);
        
        // Fallback em caso de erro
        setExpositorData({
          companyName: 'Erro ao carregar',
          cnpj: 'Não disponível',
          contactName: userProfile?.full_name || user?.full_name || 'Usuário',
          email: userProfile?.email || user?.email || 'email@exemplo.com',
          phone: 'Não disponível',
          address: 'Não disponível',
          participatingEvents: [],
          recentOrders: [],
          credenciais: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    loadExpositorData();
  }, [user, userProfile, hasRole]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="loading-container text-center p-5">
            <p>Carregando informações...</p>
          </div>
        </div>
      </div>
    );
  }

  // Componente de card de estatísticas moderno
  const ModernStatsCard = ({ title, value, icon, color, footer, delay = 0 }) => (
    <div 
      className="dashboard-stats-card"
      style={{
        '--card-color': color,
        '--animation-delay': `${delay}s`
      }}
    >
      <div className="dashboard-icon">
        {icon}
      </div>
      <div className="dashboard-content">
        <h3 className="dashboard-value">{value}</h3>
        <p className="dashboard-title">{title}</p>
        {footer && (
          <div className="dashboard-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Componente para os atalhos
  const ShortcutCard = ({ to, title, description, icon, delay = 0 }) => (
    <Link to={to} className="dashboard-stats-card" style={{ '--card-color': '#0066cc', '--animation-delay': `${delay}s` }}>
      <div className="dashboard-icon">
        {icon}
      </div>
      <div className="dashboard-content">
        <h3 className="dashboard-title">{title}</h3>
        <p className="mb-0">{description}</p>
      </div>
      <FaChevronRight className="ms-2" />
    </Link>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaUser className="dashboard-title-icon" />
              Bem-vindo, {expositorData.contactName}!
            </h1>
            <p className="dashboard-subtitle">
              Portal do Expositor - MM Eventos
            </p>
            {/* Mostrar informações do tipo de usuário */}
            <div className="mt-2">
              {hasRole('admin') && (
                <Badge bg="danger" className="me-2">Administrador</Badge>
              )}
              {hasRole('expositor') && (
                <Badge bg="primary" className="me-2">Expositor</Badge>
              )}
              {user?.systemAccess?.roleName && (
                <Badge bg="info">{user.systemAccess.roleName}</Badge>
              )}
            </div>
          </div>
        </div>
        
        {/* Alerta de erro do banco de dados se houver */}
        {dbError && (
          <div className="alert alert-warning mb-4">
            <FaInfoCircle className="me-2" />
            <strong>Aviso:</strong> {dbError}
            <br />
            <small>Exibindo dados padrão do perfil do usuário. Verifique a configuração da tabela de expositores.</small>
          </div>
        )}
        
        {/* Dados do Expositor */}
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaInfoCircle className="dashboard-section-icon" />
            Dados do Expositor
          </h2>
          <p className="dashboard-section-subtitle">
            Informações cadastrais da sua empresa
          </p>
        </div>

        <Card className="modern-table-card mb-4">
          <Card.Body className="modern-card-body">
            <Row className="info-grid">
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">Empresa:</span>
                  <span className="info-value">{expositorData.companyName}</span>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">CNPJ:</span>
                  <span className="info-value">{expositorData.cnpj}</span>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">Contato:</span>
                  <span className="info-value">{expositorData.contactName}</span>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">E-mail:</span>
                  <span className="info-value">{expositorData.email}</span>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">Telefone:</span>
                  <span className="info-value">{expositorData.phone}</span>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="info-item">
                  <span className="info-label">Endereço:</span>
                  <span className="info-value">{expositorData.address}</span>
                </div>
              </Col>
            </Row>
            
            {/* Informações de debug para desenvolvimento */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-3 bg-light border rounded">
                <h6>🔧 Informações de Debug (apenas desenvolvimento):</h6>
                <Row>
                  <Col md={6}>
                    <small>
                      <strong>User ID:</strong> {user?.id}<br/>
                      <strong>User Type:</strong> {user?.type}<br/>
                      <strong>System Role:</strong> {user?.systemAccess?.roleName}
                    </small>
                  </Col>
                  <Col md={6}>
                    <small>
                      <strong>Roles:</strong> {user?.roles?.map(r => r.name).join(', ')}<br/>
                      <strong>Permissions:</strong> {user?.permissions?.slice(0, 3).join(', ')}
                      {user?.permissions?.length > 3 && '...'}
                    </small>
                  </Col>
                </Row>
                {dbError && (
                  <div className="mt-2">
                    <small><strong>DB Error:</strong> {dbError}</small>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ExpositorHomePage;